'use client';

import { Device } from 'mediasoup-client';
import type {
  Transport,
  Producer,
  Consumer,
  RtpCapabilities,
  IceParameters,
  IceCandidate,
  DtlsParameters,
} from 'mediasoup-client/types';

type SocketEmit = (
  event: string,
  data: unknown,
  cb: (res: Record<string, unknown>) => void
) => void;

export interface MediaSession {
  device: Device;
  sendTransport: Transport | null;
  recvTransport: Transport | null;
  producer: Producer | null;
  consumer: Consumer | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
}

interface TransportServerParams {
  id: string;
  iceParameters: IceParameters;
  iceCandidates: IceCandidate[];
  dtlsParameters: DtlsParameters;
}

let session: MediaSession | null = null;

export function getMediaSession(): MediaSession | null {
  return session;
}

export async function initMediaDevice(
  callId: string,
  socketEmit: SocketEmit
): Promise<Device> {
  const device = new Device();

  const caps = await new Promise<RtpCapabilities>((resolve, reject) => {
    socketEmit(
      'media:get-capabilities',
      { callId },
      (res: Record<string, unknown>) => {
        if (res.rtpCapabilities) {
          resolve(res.rtpCapabilities as RtpCapabilities);
        } else {
          reject(new Error('Failed to get RTP capabilities'));
        }
      }
    );
  });

  await device.load({ routerRtpCapabilities: caps });

  session = {
    device,
    sendTransport: null,
    recvTransport: null,
    producer: null,
    consumer: null,
    localStream: null,
    remoteStream: null,
  };

  return device;
}

export async function createSendTransport(
  callId: string,
  socketEmit: SocketEmit
): Promise<Transport> {
  if (!session) throw new Error('Device not initialized');

  const serverParams = await new Promise<TransportServerParams>(
    (resolve, reject) => {
      socketEmit(
        'media:create-transport',
        { callId, direction: 'send' },
        (res) => {
          if (res.error) reject(new Error(String(res.error)));
          else resolve(res as unknown as TransportServerParams);
        }
      );
    }
  );

  const transport = session.device.createSendTransport(serverParams);

  transport.on('connect', ({ dtlsParameters }, callback, errback) => {
    socketEmit(
      'media:connect-transport',
      { callId, transportId: transport.id, dtlsParameters },
      (res) => {
        if (res.connected) callback();
        else errback(new Error('Transport connect failed'));
      }
    );
  });

  transport.on('produce', ({ kind, rtpParameters }, callback, errback) => {
    socketEmit(
      'media:produce',
      { callId, kind, rtpParameters },
      (res) => {
        if (res.producerId) callback({ id: res.producerId as string });
        else errback(new Error('Produce failed'));
      }
    );
  });

  session.sendTransport = transport;
  return transport;
}

export async function createRecvTransport(
  callId: string,
  socketEmit: SocketEmit
): Promise<Transport> {
  if (!session) throw new Error('Device not initialized');

  const serverParams = await new Promise<TransportServerParams>(
    (resolve, reject) => {
      socketEmit(
        'media:create-transport',
        { callId, direction: 'recv' },
        (res) => {
          if (res.error) reject(new Error(String(res.error)));
          else resolve(res as unknown as TransportServerParams);
        }
      );
    }
  );

  const transport = session.device.createRecvTransport(serverParams);

  transport.on('connect', ({ dtlsParameters }, callback, errback) => {
    socketEmit(
      'media:connect-transport',
      { callId, transportId: transport.id, dtlsParameters },
      (res) => {
        if (res.connected) callback();
        else errback(new Error('Recv transport connect failed'));
      }
    );
  });

  session.recvTransport = transport;
  return transport;
}

export async function produceMicAudio(): Promise<Producer> {
  if (!session?.sendTransport) throw new Error('Send transport not ready');

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      sampleRate: 48000,
      channelCount: 1,
    },
    video: false,
  });

  const track = stream.getAudioTracks()[0];

  const producer = await session.sendTransport.produce({
    track,
    codecOptions: { opusStereo: false, opusDtx: true },
  });

  session.producer = producer;
  session.localStream = stream;

  return producer;
}

export async function consumeRemoteAudio(
  callId: string,
  producerUserId: string,
  socketEmit: SocketEmit
): Promise<Consumer> {
  if (!session?.recvTransport || !session.device) {
    throw new Error('Recv transport not ready');
  }

  const consumerData = await new Promise<Record<string, unknown>>(
    (resolve, reject) => {
      socketEmit(
        'media:consume',
        {
          callId,
          producerUserId,
          rtpCapabilities: session!.device.rtpCapabilities,
        },
        (res) => {
          if (res.error) reject(new Error(String(res.error)));
          else resolve(res);
        }
      );
    }
  );

  const consumer = await session.recvTransport.consume({
    id: consumerData.id as string,
    producerId: consumerData.producerId as string,
    kind: consumerData.kind as 'audio',
    rtpParameters: consumerData.rtpParameters as unknown as import('mediasoup-client/types').RtpParameters,
  });

  const remoteStream = new MediaStream([consumer.track]);
  session.consumer = consumer;
  session.remoteStream = remoteStream;

  const audio = new Audio();
  audio.srcObject = remoteStream;
  audio.autoplay = true;
  await audio.play().catch(() => {});

  return consumer;
}

export function toggleMute(muted: boolean): void {
  if (!session?.producer) return;
  if (muted) {
    session.producer.pause();
  } else {
    session.producer.resume();
  }
}

export function closeMediaSession(): void {
  if (!session) return;

  session.producer?.close();
  session.consumer?.close();
  session.sendTransport?.close();
  session.recvTransport?.close();

  if (session.localStream) {
    for (const track of session.localStream.getTracks()) {
      track.stop();
    }
  }

  session = null;
}

export function getAudioLevel(): number {
  if (!session?.localStream) return 0;

  const ctx = new AudioContext();
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 256;
  const source = ctx.createMediaStreamSource(session.localStream);
  source.connect(analyser);

  const data = new Uint8Array(analyser.frequencyBinCount);
  analyser.getByteFrequencyData(data);

  let sum = 0;
  for (let i = 0; i < data.length; i++) {
    sum += data[i];
  }

  source.disconnect();
  ctx.close();

  return Math.round((sum / data.length / 255) * 100);
}
